/**
 * CEO Command Center - Layout Components
 * 
 * Main layout structure components:
 * - AppLayout: Main application shell
 * - Header: Top navigation bar
 * - Sidebar: Navigation sidebar with dynamic domains
 * - CommandPalette: Cmd+K quick actions
 * 
 * @module components/layout
 * @version 10.2.0
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { cn, getGreeting } from '../../utils/format';
import { Button, Modal, Input } from '../ui';
import { ConnectionStatus, ProfileDropdown, SyncStatus, SyncSettingsPanel } from '../common';
import { DOMAIN_CONFIG } from '../../config/constants';
import { useInitiativeStore } from '../../stores/initiativeStore';
import { useDomainStore, useActiveDomains } from '../../stores/domainStore';
import type { ViewId, User, Signal } from '../../types';
import type { InitiativeWithMetrics } from '../../types/initiative';

// TYPES

interface NavigationItem {
  id: ViewId | string;
  label: string;
  icon: string;
  group?: string;
}

// STATIC NAVIGATION CONFIG (non-domain items)
const STATIC_NAVIGATION: NavigationItem[] = [
  { id: 'command', label: 'Command Center', icon: '🏠', group: 'main' },
  // STRATEGIC section
  { id: 'pipeline', label: 'Sales', icon: '💼', group: 'strategic' },
  { id: 'fundraising', label: 'Fundraising', icon: '💰', group: 'strategic' },
  { id: 'cashflow', label: 'Cash Flow', icon: '💵', group: 'strategic' },
  { id: 'board', label: 'Board', icon: '🏛️', group: 'strategic' },
  // ANALYTICS section
  { id: 'mau', label: 'MAU Analytics', icon: '📊', group: 'analytics' },
  // TOOLS section
  { id: 'settings', label: 'Settings', icon: '⚙️', group: 'tools' },
];

// Static navigation for command palette (includes default domains)
const COMMAND_PALETTE_NAV: NavigationItem[] = [
  { id: 'command', label: 'Command Center', icon: '🏠' },
  { id: 'emg', label: 'EMG', icon: '👥' },
  { id: 'product', label: 'Product', icon: '🔧' },
  { id: 'growth', label: 'Growth', icon: '📈' },
  { id: 'operations', label: 'Operations', icon: '⚙️' },
  { id: 'finance', label: 'Finance', icon: '💵' },
  { id: 'pipeline', label: 'Sales', icon: '💼' },
  { id: 'fundraising', label: 'Fundraising', icon: '💰' },
  { id: 'cashflow', label: 'Cash Flow', icon: '💵' },
  { id: 'board', label: 'Board', icon: '🏛️' },
  { id: 'mau', label: 'MAU Analytics', icon: '📊' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
];

// HEADER

interface HeaderProps {
  user?: User | null;
  onLogout?: () => void;
  onOpenSettings?: () => void;
  onOpenTestSuite?: () => void;
  onOpenCommandPalette?: () => void;
  signalCount?: number;
  className?: string;
}

/**
 * Application header with user info and actions
 */
export const Header: React.FC<HeaderProps> = ({
  user,
  onLogout,
  onOpenSettings,
  onOpenTestSuite,
  onOpenCommandPalette,
  signalCount = 0,
  className,
}) => {
  const [syncSettingsOpen, setSyncSettingsOpen] = useState(false);

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <header className={cn(
        'h-16 bg-white border-b flex items-center justify-between px-6',
        className
      )}>
        {/* Left: Greeting */}
        <div>
          <h1 className="text-lg font-semibold text-gray-900">
            {getGreeting()}, {user?.name || 'there'}
          </h1>
          <p className="text-sm text-gray-500">{today}</p>
        </div>

        {/* Right: Status + Actions */}
        <div className="flex items-center gap-4">
          {/* P2P Sync Status (v11) */}
          <SyncStatus compact onOpenSettings={() => setSyncSettingsOpen(true)} />

          {/* API Connection Status */}
          <ConnectionStatus />

          {/* Signal indicator */}
          {signalCount > 0 && (
            <div className="flex items-center gap-1 px-3 py-1 bg-primary-50 text-primary-600 rounded-full">
              <span className="text-sm font-medium">{signalCount}</span>
              <span className="text-xs">signals</span>
            </div>
          )}

          {/* Profile dropdown (includes settings) */}
          {user && onLogout && onOpenSettings && (
            <ProfileDropdown
              user={user}
              onLogout={onLogout}
              onOpenSettings={onOpenSettings}
              onOpenTestSuite={onOpenTestSuite}
            />
          )}
        </div>
      </header>

      {/* Sync Settings Panel */}
      <SyncSettingsPanel
        isOpen={syncSettingsOpen}
        onClose={() => setSyncSettingsOpen(false)}
      />
    </>
  );
};

// SIDEBAR

interface SidebarProps {
  currentView: ViewId;
  onNavigate: (view: ViewId) => void;
  selectedInitiativeId?: string | null;
  onSelectInitiative?: (id: string) => void;
  onAddInitiative?: () => void;
  onAddDomain?: () => void;
  onDeleteDomain?: (id: string) => void;
  onDeleteInitiative?: (id: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
}

// Delete confirmation state
interface DeleteConfirmation {
  type: 'domain' | 'initiative';
  id: string;
  name: string;
}

/**
 * Navigation sidebar with dynamic domain and initiative support
 */
export const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  onNavigate,
  selectedInitiativeId,
  onSelectInitiative,
  onAddInitiative,
  onAddDomain,
  onDeleteDomain,
  onDeleteInitiative,
  collapsed = false,
  onToggleCollapse,
  className,
}) => {
  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmation | null>(null);
  
  // Get initiatives from store (with fallback to empty array)
  const initiatives = useInitiativeStore(state => 
    (state.initiatives || []).map(i => state.getInitiativeWithMetrics(i.id)!).filter(Boolean)
  );
  
  // Get active domains from store
  const activeDomains = useActiveDomains();
  
  // Build navigation items dynamically
  const NAVIGATION = useMemo(() => {
    // Get static items (excluding business which is now dynamic)
    const staticItems = STATIC_NAVIGATION;
    
    // Generate business items from domains
    const businessItems: NavigationItem[] = activeDomains.map(domain => ({
      id: domain.id.toLowerCase() as ViewId,
      label: domain.name,
      icon: domain.icon,
      group: 'business',
    }));
    
    return [...staticItems.filter(n => n.group === 'main'), ...businessItems, ...staticItems.filter(n => n.group !== 'main')];
  }, [activeDomains]);
  
  // Navigation groups
  const groups = [
    { id: 'main', label: '' },
    { id: 'business', label: 'Business' },
    { id: 'strategic', label: 'Strategic' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'tools', label: 'Tools' },
  ];
  
  // Check if viewing an initiative
  const isInitiativeView = currentView === 'initiative' || currentView === 'aia';
  
  return (
    <aside className={cn(
      'bg-gray-900 text-white flex flex-col h-full overflow-hidden transition-all duration-300',
      collapsed ? 'w-16' : 'w-64',
      className
    )}>
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-800">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎯</span>
            <span className="font-bold">CEO Command</span>
          </div>
        )}
        {collapsed && <span className="text-2xl mx-auto">🎯</span>}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            className="p-1 text-gray-400 hover:text-white rounded"
          >
            {collapsed ? '→' : '←'}
          </button>
        )}
      </div>
      
      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* Standard navigation groups */}
        {groups.map(group => {
          const items = NAVIGATION.filter(n => n.group === group.id);
          if (items.length === 0 && group.id !== 'business') return null;
          
          return (
            <div key={group.id} className="mb-4">
              {group.label && !collapsed && (
                <p className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {group.label}
                </p>
              )}
              {items.map(item => {
                return (
                  <div
                    key={item.id}
                    className="group relative flex items-center"
                  >
                    <button
                      onClick={() => onNavigate(item.id)}
                      className={cn(
                        'flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                        currentView === item.id && !isInitiativeView
                          ? 'bg-primary-500 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      )}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="text-lg">{item.icon}</span>
                      {!collapsed && (
                        <span className="text-sm font-medium">{item.label}</span>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
        
        {/* INITIATIVES Section */}
        <div className="mb-4">
          {!collapsed && (
            <p className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Initiatives
            </p>
          )}
          
          {/* Initiative items */}
          {initiatives.map(initiative => (
            <div
              key={initiative.id}
              className="group relative flex items-center"
            >
              <button
                onClick={() => onSelectInitiative?.(initiative.id)}
                className={cn(
                  'flex-1 flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                  isInitiativeView && selectedInitiativeId === initiative.id
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                )}
                title={collapsed ? initiative.name : undefined}
              >
                <span className="text-lg">{initiative.icon}</span>
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium truncate block">{initiative.name}</span>
                    <span className={cn(
                      'text-xs',
                      isInitiativeView && selectedInitiativeId === initiative.id
                        ? 'text-primary-200'
                        : 'text-gray-500'
                    )}>
                      {initiative.completedTasks}/{initiative.totalTasks} tasks
                    </span>
                  </div>
                )}
                {!collapsed && initiative.inProgressTasks > 0 && (
                  <span className={cn(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    isInitiativeView && selectedInitiativeId === initiative.id
                      ? 'bg-white'
                      : 'bg-amber-500'
                  )} />
                )}
              </button>
            </div>
          ))}
        </div>
      </nav>
      
      {/* Footer */}
      <div className="p-4 border-t border-gray-800">
        {!collapsed && (
          <p className="text-xs text-gray-500">
            v11.0.0 • Powered by Strives
          </p>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title={`Delete ${deleteConfirm?.type === 'domain' ? 'Domain' : 'Initiative'}`}
      >
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-amber-800 font-medium">⚠️ Are you sure?</p>
          </div>
          
          <p className="text-gray-700">
            Are you sure you want to hide <strong>"{deleteConfirm?.name}"</strong>?
          </p>
          
          {deleteConfirm?.type === 'domain' && (
            <p className="text-sm text-gray-500">
              This will hide the domain from your sidebar. You can restore it later from Settings. Tasks associated with this domain will remain in Airtable.
            </p>
          )}
          
          {deleteConfirm?.type === 'initiative' && (
            <p className="text-sm text-gray-500">
              This will permanently delete the initiative and all its associated tasks and documents from local storage.
            </p>
          )}
          
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                if (deleteConfirm) {
                  if (deleteConfirm.type === 'domain' && onDeleteDomain) {
                    onDeleteDomain(deleteConfirm.id);
                  } else if (deleteConfirm.type === 'initiative' && onDeleteInitiative) {
                    onDeleteInitiative(deleteConfirm.id);
                  }
                  setDeleteConfirm(null);
                }
              }}
            >
              {deleteConfirm?.type === 'domain' ? 'Hide Domain' : 'Delete Initiative'}
            </Button>
          </div>
        </div>
      </Modal>
    </aside>
  );
};

// COMMAND PALETTE

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate: (view: ViewId) => void;
  onAction?: (action: string) => void;
}

interface CommandItem {
  id: string;
  label: string;
  icon: string;
  shortcut?: string;
  action: () => void;
  category: 'navigation' | 'action' | 'recent';
}

/**
 * Command palette (Cmd+K) for quick actions
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
  onNavigate,
  onAction,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Build command list
  const commands: CommandItem[] = [
    // Quick actions
    { id: 'new-task', label: 'New Task', icon: '➕', shortcut: '⌘N', action: () => onAction?.('new-task'), category: 'action' },
    { id: 'new-deal', label: 'New Deal', icon: '💼', shortcut: '⌘D', action: () => onAction?.('new-deal'), category: 'action' },
    { id: 'ai-chat', label: 'Ask AI', icon: '🤖', shortcut: '⌘/', action: () => onAction?.('ai-chat'), category: 'action' },
    { id: 'refresh', label: 'Refresh Data', icon: '🔄', shortcut: '⌘R', action: () => onAction?.('refresh'), category: 'action' },
    // Navigation
    ...COMMAND_PALETTE_NAV.map(nav => ({
      id: `nav-${nav.id}`,
      label: nav.label,
      icon: nav.icon,
      action: () => { onNavigate(nav.id as ViewId); onClose(); },
      category: 'navigation' as const,
    })),
  ];
  
  // Filter commands based on search
  const filteredCommands = search
    ? commands.filter(cmd => 
        cmd.label.toLowerCase().includes(search.toLowerCase())
      )
    : commands;
  
  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          filteredCommands[selectedIndex].action();
          onClose();
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [filteredCommands, selectedIndex, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Palette */}
      <div className="flex items-start justify-center pt-[15vh]">
        <div 
          className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b">
            <span className="text-gray-400">🔍</span>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search commands..."
              className="flex-1 text-lg outline-none placeholder-gray-400"
            />
            <kbd className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">ESC</kbd>
          </div>
          
          {/* Commands list */}
          <div className="max-h-80 overflow-y-auto py-2">
            {filteredCommands.length === 0 ? (
              <div className="px-4 py-8 text-center text-gray-500">
                No commands found
              </div>
            ) : (
              <>
                {/* Actions */}
                {filteredCommands.filter(c => c.category === 'action').length > 0 && (
                  <div>
                    <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
                      Quick Actions
                    </p>
                    {filteredCommands
                      .filter(c => c.category === 'action')
                      .map((cmd, idx) => (
                        <CommandItem
                          key={cmd.id}
                          command={cmd}
                          isSelected={idx === selectedIndex}
                          onClick={() => { cmd.action(); onClose(); }}
                        />
                      ))}
                  </div>
                )}
                
                {/* Navigation */}
                {filteredCommands.filter(c => c.category === 'navigation').length > 0 && (
                  <div>
                    <p className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
                      Navigation
                    </p>
                    {filteredCommands
                      .filter(c => c.category === 'navigation')
                      .map((cmd, idx) => {
                        const actionCount = filteredCommands.filter(c => c.category === 'action').length;
                        return (
                          <CommandItem
                            key={cmd.id}
                            command={cmd}
                            isSelected={idx + actionCount === selectedIndex}
                            onClick={() => { cmd.action(); onClose(); }}
                          />
                        );
                      })}
                  </div>
                )}
              </>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-400 flex items-center gap-4">
            <span>↑↓ Navigate</span>
            <span>↵ Select</span>
            <span>ESC Close</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const CommandItem: React.FC<{
  command: CommandItem;
  isSelected: boolean;
  onClick: () => void;
}> = ({ command, isSelected, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'w-full flex items-center justify-between px-4 py-2 text-left transition-colors',
      isSelected ? 'bg-primary-50 text-primary-600' : 'hover:bg-gray-50'
    )}
  >
    <div className="flex items-center gap-3">
      <span>{command.icon}</span>
      <span className="font-medium">{command.label}</span>
    </div>
    {command.shortcut && (
      <kbd className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
        {command.shortcut}
      </kbd>
    )}
  </button>
);

// APP LAYOUT

interface AppLayoutProps {
  children: React.ReactNode;
  currentView: ViewId;
  onNavigate: (view: ViewId) => void;
  user?: User | null;
  onLogout?: () => void;
  signalCount?: number;
  onAction?: (action: string) => void;
  selectedInitiativeId?: string | null;
  onSelectInitiative?: (id: string) => void;
  onAddInitiative?: () => void;
  onAddDomain?: () => void;
  onDeleteDomain?: (id: string) => void;
  onDeleteInitiative?: (id: string) => void;
}

/**
 * Main application layout shell
 */
export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  currentView,
  onNavigate,
  user,
  onLogout,
  signalCount = 0,
  onAction,
  selectedInitiativeId,
  onSelectInitiative,
  onAddInitiative,
  onAddDomain,
  onDeleteDomain,
  onDeleteInitiative,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  
  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K: Command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      // Cmd+N: New task
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        onAction?.('new-task');
      }
      // Cmd+D: New deal
      if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        onAction?.('new-deal');
      }
      // Cmd+/: AI chat
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        onAction?.('ai-chat');
      }
      // Cmd+,: Settings
      if ((e.metaKey || e.ctrlKey) && e.key === ',') {
        e.preventDefault();
        onNavigate('settings' as ViewId);
      }
      // Number keys for navigation
      if ((e.metaKey || e.ctrlKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        if (COMMAND_PALETTE_NAV[index]) {
          onNavigate(COMMAND_PALETTE_NAV[index].id as ViewId);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNavigate, onAction]);
  
  return (
    <div className="h-screen flex bg-gray-50">
      {/* Sidebar */}
      <Sidebar
        currentView={currentView}
        onNavigate={onNavigate}
        selectedInitiativeId={selectedInitiativeId}
        onSelectInitiative={onSelectInitiative}
        onAddInitiative={onAddInitiative}
        onAddDomain={onAddDomain}
        onDeleteDomain={onDeleteDomain}
        onDeleteInitiative={onDeleteInitiative}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <Header
          user={user}
          onLogout={onLogout}
          onOpenSettings={() => onNavigate('settings' as ViewId)}
          onOpenTestSuite={() => onNavigate('test' as ViewId)}
          onOpenCommandPalette={() => setCommandPaletteOpen(true)}
          signalCount={signalCount}
        />
        
        {/* Content */}
        <main className="flex-1 overflow-y-auto p-8">
          {children}
        </main>
      </div>
      
      {/* Command Palette */}
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onNavigate={onNavigate}
        onAction={onAction}
      />
    </div>
  );
};

